import { useState, useRef, useEffect } from 'react'
import { ArrowLeft, FileText, Video, ChevronDown, ChevronRight } from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { MANUAL_IMAGES, VIDEOS } from '../config/assets'

const AirHeaterImage = MANUAL_IMAGES.AIR_HEATER
const AirHeaterDiagram = MANUAL_IMAGES.AIR_HEATER_STRUCTURE
const AirHeaterInstallationVideo = VIDEOS.AIR_HEATER_INSTALL
const AirHeaterMaintenanceVideo = VIDEOS.AIR_HEATER_CLEAN

export default function AirHeaterManualPage() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const instructionsRef = useRef<HTMLDivElement>(null)
  const videosRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const handlePreviewManual = () => {
    // 展开所有使用说明部分
    setExpandedSections({
      overview: true,
      structure: true,
      installation: true,
      operation: true,
      maintenance: true,
      troubleshooting: true,
      safety: true
    })
    // 滚动到使用说明部分，添加偏移量
    if (instructionsRef.current) {
      const element = instructionsRef.current
      const offset = 100 // 调整这个值来控制滚动位置
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  const handleWatchVideo = () => {
    // 滚动到操作视频部分，添加偏移量
    if (videosRef.current) {
      const element = videosRef.current
      const offset = 100 // 调整这个值来控制滚动位置
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section ref={sectionRef} className="py-20 lg:py-28 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className={`mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-secondary-200 text-secondary-700 hover:bg-secondary-50 transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>返回资料中心</span>
            </button>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-secondary-900 mb-4">
              风暖炉使用说明书
            </h1>
            <p className="text-lg text-secondary-600 mb-8">
              高效节能 · 智能恒温 · 环保安全
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: Product Image */}
            <div className="lg:col-span-1">
              <div className={`bg-white rounded-2xl overflow-hidden shadow-lg transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '100ms' }}>
                <img
                  src={AirHeaterImage}
                  alt="风暖炉"
                  className="w-full h-auto object-contain"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-secondary-900 mb-4">规格参数</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">电压</span>
                      <span className="font-medium text-secondary-900">220V</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">频率</span>
                      <span className="font-medium text-secondary-900">50Hz</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">热效率</span>
                      <span className="font-medium text-secondary-900">约90-95%</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">额定功率</span>
                      <span className="font-medium text-secondary-900">请参考产品铭牌</span>
                    </div>
                    <div className="flex justify-between border-b border-secondary-100 pb-2">
                      <span className="text-secondary-600">外形尺寸和重量</span>
                      <span className="font-medium text-secondary-900">请参考产品铭牌</span>
                    </div>
                    <div className="flex justify-between pb-2">
                      <span className="text-secondary-600">燃料类型</span>
                      <span className="font-medium text-secondary-900">生物质颗粒 (直径6-10mm; 长度1-5cm; 灰份≤10%)</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <button 
                      onClick={handlePreviewManual}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors"
                    >
                      <FileText className="w-5 h-5" />
                      预览说明书
                    </button>
                    <button 
                      onClick={handleWatchVideo}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-secondary-100 text-secondary-700 font-semibold hover:bg-secondary-200 transition-colors"
                    >
                      <Video className="w-5 h-5" />
                      观看操作视频
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Details */}
            <div className="lg:col-span-2">
              {/* Structure Diagram */}
              <div className={`bg-white rounded-2xl overflow-hidden shadow-lg mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-secondary-900 mb-4">产品结构图</h3>
                  <img
                    src={AirHeaterDiagram}
                    alt="风暖炉结构图"
                    className="w-full rounded-lg mb-4"
                  />
                  <p className="text-secondary-600 text-sm">
                    风暖炉结构示意图，展示了设备的主要组成部分和工作原理
                  </p>
                </div>
              </div>

              {/* Sections */}
              <div 
                ref={instructionsRef}
                className={`bg-white rounded-2xl overflow-hidden shadow-lg transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} 
                style={{ transitionDelay: '300ms' }}
              >
                <div className="p-6">
                  <h3 className="text-xl font-bold text-secondary-900 mb-6">使用说明</h3>
                  
                  <div className="space-y-4">
                    {/* 产品概述 */}
                    <div className="border-b border-secondary-100 pb-4">
                      <button
                        onClick={() => toggleSection('overview')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">1. 产品概述</h4>
                        {expandedSections['overview'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['overview'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          风暖颗粒炉是一种以生物质颗粒为燃料的高效供暖设备，采用先进的燃烧技术和强制送风系统，具有热效率高、环保节能、智能控制等特点。适用于车间、仓库、养殖场、温室等大空间场所的供暖需求。
                        </p>
                      )}
                    </div>

                    {/* 产品结构 */}
                    <div className="border-b border-secondary-100 pb-4">
                      <button
                        onClick={() => toggleSection('structure')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">2. 产品结构</h4>
                        {expandedSections['structure'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['structure'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          风暖炉主要由以下部分组成：1. 进料系统 2. 燃烧系统 3. 送风系统 4. 控制系统 5. 外壳结构。各部分协调工作，确保设备稳定高效运行。
                        </p>
                      )}
                    </div>

                    {/* 安装说明 */}
                    <div className="border-b border-secondary-100 pb-4">
                      <button
                        onClick={() => toggleSection('installation')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">3. 安装说明</h4>
                        {expandedSections['installation'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['installation'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          1. 选择合适的安装位置，确保通风良好 2. 安装排烟管道，确保排烟顺畅 3. 连接电源和控制系统 4. 检查各部件连接是否牢固 5. 进行试运行和调试。
                        </p>
                      )}
                    </div>

                    {/* 操作方法 */}
                    <div className="border-b border-secondary-100 pb-4">
                      <button
                        onClick={() => toggleSection('operation')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">4. 操作方法</h4>
                        {expandedSections['operation'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['operation'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          1. 打开电源开关 2. 设置温度参数 3. 启动进料系统 4. 点燃燃料 5. 调节送风速度 6. 监控运行状态 7. 关机时先停止进料，待燃料燃尽后再关闭电源。
                        </p>
                      )}
                    </div>

                    {/* 维护保养 */}
                    <div className="border-b border-secondary-100 pb-4">
                      <button
                        onClick={() => toggleSection('maintenance')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">5. 维护保养</h4>
                        {expandedSections['maintenance'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['maintenance'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          1. 定期清理炉膛和烟道 2. 检查进料系统是否堵塞 3. 清理风机和过滤器 4. 检查密封件是否损坏 5. 定期检查电气系统 6. 冬季使用后进行全面保养。
                        </p>
                      )}
                    </div>

                    {/* 常见故障 */}
                    <div className="border-b border-secondary-100 pb-4">
                      <button
                        onClick={() => toggleSection('troubleshooting')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">6. 常见故障</h4>
                        {expandedSections['troubleshooting'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['troubleshooting'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          1. 点火失败：检查点火器、燃料质量 2. 温度不达标：检查燃料供应、送风系统 3. 噪音异常：检查风机、电机 4. 排烟异常：检查烟道、烟囱 5. 控制系统故障：检查电源、传感器。
                        </p>
                      )}
                    </div>

                    {/* 安全注意事项 */}
                    <div className="pb-4">
                      <button
                        onClick={() => toggleSection('safety')}
                        className="w-full flex items-center justify-between text-left mb-2"
                      >
                        <h4 className="text-lg font-semibold text-secondary-900">7. 安全注意事项</h4>
                        {expandedSections['safety'] ? (
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-secondary-500" />
                        )}
                      </button>
                      {expandedSections['safety'] && (
                        <p className="text-secondary-600 leading-relaxed pl-4">
                          1. 安装时确保通风良好 2. 远离易燃物品 3. 定期检查设备状态 4. 操作时佩戴防护装备 5. 儿童和宠物远离设备 6. 遇到故障及时停机检查 7. 专业人员进行维修。
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Videos */}
              <div 
                ref={videosRef}
                className={`bg-white rounded-2xl overflow-hidden shadow-lg mt-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} 
                style={{ transitionDelay: '400ms' }}
              >
                <div className="p-6">
                  <h3 className="text-xl font-bold text-secondary-900 mb-6">操作视频</h3>
                  
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="border border-secondary-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                      <div className="aspect-[9/16] relative bg-black">
                        <video 
                          src={AirHeaterInstallationVideo} 
                          className="w-full h-full object-cover"
                          controls
                          poster={AirHeaterImage}
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold text-secondary-900 mb-1">风暖炉安装使用教程</h4>
                        <div className="flex items-center justify-between text-sm text-secondary-500">
                          <span>15:30</span>
                          <button className="text-primary-600 hover:text-primary-700 transition-colors">
                            观看视频
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="border border-secondary-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                      <div className="aspect-[9/16] relative bg-black">
                        <video 
                          src={AirHeaterMaintenanceVideo} 
                          className="w-full h-full object-cover"
                          controls
                          poster={AirHeaterImage}
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold text-secondary-900 mb-1">颗粒风暖炉保养教程</h4>
                        <div className="flex items-center justify-between text-sm text-secondary-500">
                          <span>10:20</span>
                          <button className="text-primary-600 hover:text-primary-700 transition-colors">
                            观看视频
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="border border-secondary-200 rounded-lg overflow-hidden hover:shadow-md transition-all">
                      <div className="aspect-[9/16] bg-gray-200 relative flex items-center justify-center">
                        <Video className="w-12 h-12 text-primary-600" />
                      </div>
                      <div className="p-4">
                        <h4 className="font-semibold text-secondary-900 mb-1">风暖炉常见故障排查</h4>
                        <div className="flex items-center justify-between text-sm text-secondary-500">
                          <span>8:45</span>
                          <button className="text-primary-600 hover:text-primary-700 transition-colors">
                            观看视频
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className={`mt-16 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-4 p-6 rounded-2xl bg-white border border-secondary-100 shadow-sm">
              <div className="p-3 rounded-xl bg-primary-100">
                <FileText className="w-6 h-6 text-primary-600" />
              </div>
              <div className="text-left">
                <h4 className="text-xl font-bold text-secondary-900 mb-2">需要更多帮助？</h4>
                <p className="text-secondary-600">
                  如有任何使用问题或技术支持需求，请联系我们的客服团队
                </p>
              </div>
              <button className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors">
                联系客服
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}